/*
 *
 * UnassignedApplicationsCard
 *
 */

import moment from 'moment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CanceledIcon from '@mui/icons-material/Cancel';
import HourglassIcon from '@mui/icons-material/HourglassEmpty';
import { FormattedMessage, useIntl } from 'react-intl';
import ScrollablePaper from '~/components/ScrollablePaper';
import VehicleTag from '~/components/Tag/Vehicle';
import TagList from '~/components/TagList';
import { recurrenceTypes, parseWeekday } from '~/components/RecurrenceRules';
import { ApplicationSummaryResource } from '~/generated';
import ListItemButton from '@mui/material/ListItemButton';
import { createApplicationInfoString } from './UpcomingAssignmentsCard';
import messages from './messages';
import { RecurrentApplicationRule } from '../AvailabilityList';
import { Link } from '@tanstack/react-router';

type UnassignedApplicationsCardProps = {
  unassignedApplications: ApplicationSummaryResource[];
};

export const UnassignedApplicationsCard = (
  props: UnassignedApplicationsCardProps
) => {
  const intl = useIntl();
  const navigateToApplication = (application: ApplicationSummaryResource) => {
    const shiftDate = moment(application.shiftStartDate);
    return `/application/${shiftDate.isoWeekYear()}/${shiftDate.isoWeek()}/${shiftDate.weekday()}`;
  };

  const createSecondaryText = (application: ApplicationSummaryResource) => {
    if (application.shiftIsCanceled) {
      return intl.formatMessage(messages.shiftCanceled);
    }

    if (application.recurrence) {
      const recurrence = application.recurrence as RecurrentApplicationRule;

      switch (recurrence.type) {
        case recurrenceTypes.weekly:
          return intl.formatMessage(messages.regularApplicationWeekly, {
            nthWeek: recurrence.interval,
          });
        case recurrenceTypes.nthWeekdayOfMonth:
          return intl.formatMessage(messages.regularApplicationMonthly, {
            nth: recurrence.nthWeekdayOfMonth,
            weekday: moment()
              .isoWeekday(parseWeekday(recurrence.weekday))
              .format('dddd'),
          });
      }
    }

    return intl.formatMessage(messages.appliedOn, {
      appliedOn: moment(application.appliedOn).format('LLL'),
    });
  };
  return (
    <div data-test="unassigned-applications-card">
      <ScrollablePaper
        title={<FormattedMessage {...messages.unassignedApplications} />}
      >
        <List style={{ padding: 0 }}>
          {props.unassignedApplications.length > 0 ? (
            props.unassignedApplications.map((application) => (
              <ListItemButton
                key={application.shiftId}
                to={navigateToApplication(application)}
                component={Link}
              >
                <ListItemIcon>
                  {application.shiftIsCanceled ? (
                    <CanceledIcon />
                  ) : (
                    <HourglassIcon />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={createApplicationInfoString(application)}
                  slotProps={{
                    secondary: { component: 'div' },
                  }}
                  secondary={
                    <TagList>
                      <span>{createSecondaryText(application)}</span>
                      {application.appliedWithCar && <VehicleTag />}
                    </TagList>
                  }
                />
              </ListItemButton>
            ))
          ) : (
            <ListItem>
              <ListItemText
                primary={intl.formatMessage(messages.noUnassignedApplications)}
              />
            </ListItem>
          )}
        </List>
      </ScrollablePaper>
    </div>
  );
};

export default UnassignedApplicationsCard;
